import * as React from 'react'
import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select } from 'antd'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import { useStore } from 'effector-react'
import { useCommentForm } from '../../../../views/widgets/commentForm/hooks'
import { comment, submitForm } from '../../../../views/widgets/commentForm/model'
import { $userRole } from '../../../../features/user'

type Props = {
  open: boolean
  onClose: () => void
  entityType: string
  entityId: string
}

export const VerificationCommentModal = ({
  open,
  onClose,
  entityType,
  entityId,
}: Props) => {
  const [form] = Form.useForm()
  const { form: commentForm } = useCommentForm()
  const { loading, error } = commentForm
  const userRole = useStore($userRole)
  const messageInputRef = React.useRef<TextAreaRef>(null)

  useEffect(() => {
    const unwatch = comment.doneData.watch(() => {
      form.resetFields()
      onClose()
    })
    return () => unwatch()
  }, [onClose])

  useEffect(() => {
    if (!open) return

    const frameId = requestAnimationFrame(() => {
      messageInputRef.current?.focus()
    })

    return () => cancelAnimationFrame(frameId)
  }, [open])

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      submitForm({
        entityType,
        entityId,
        message: values.message,
        visibility: values.visibility,
        department: userRole?.[0] || '',
        type: Number(values.type),
      })
    })
  }

  return (
    <Modal
      title="Leave comment"
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Save"
      okButtonProps={{ loading }}
      afterOpenChange={(visible) => {
        if (visible) {
          requestAnimationFrame(() => messageInputRef.current?.focus())
        }
      }}
    >
      <Form form={form} layout="vertical" initialValues={{ visibility: 'all', type: '2' }}>
        <Form.Item
          name="message"
          label="Comment"
          rules={[{ required: true, message: 'Field is required' }]}
        >
          <Input.TextArea
            ref={messageInputRef}
            autoFocus
            placeholder="Comment"
            rows={4}
          />
        </Form.Item>
        <Form.Item name="visibility" label="To whom?" rules={[{ required: true }]}>
          <Select>
            <Select.Option value="all">All</Select.Option>
            <Select.Option value="support">Support</Select.Option>
            <Select.Option value="finance">Finance</Select.Option>
            <Select.Option value="dealing">Dealing</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="type" label="Status" rules={[{ required: true }]}>
          <Select>
            <Select.Option value="2">Info</Select.Option>
            <Select.Option value="1">Warning</Select.Option>
            <Select.Option value="0">Alert</Select.Option>
          </Select>
        </Form.Item>
        {error && (
          <Alert type="error" message={error?.msg} showIcon />
        )}
      </Form>
    </Modal>
  )
}
